import { PlusOutlined } from "@ant-design/icons";
import { Button, Checkbox, Form, Grid, Modal, Select, Space, Spin } from "antd";
import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { PATHS } from "../../../app/router/paths";
import { PageHeader } from "../../../shared/components/PageHeader";
import { ProposeBasicInfoFields } from "../components/ProposeBasicInfoFields";
import { useProposeEditor } from "../hooks/useProposeEditor";
import "../styles/Proposes.css";

/** The coordinator assigns only products. Commercial item fields are owned by Sale. */
export const ProposeEditPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const id = useMemo(() => Number(params.get("id")) || undefined, [params]);
  const representativeId = params.get("representative_id") || undefined;
  const {
    form, customers, recipients, products, productOptions, mobileItems, loading,
    saving, searchProductOptions, save,
  } = useProposeEditor({ id, representativeId, isMobile });
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  const [productCodesToAdd, setProductCodesToAdd] = useState<string[]>([]);
  const [selectedItemProductCodes, setSelectedItemProductCodes] = useState<Set<string>>(new Set());

  const saveProposal = async (status: "DRAFT" | "PUBLISH") => {
    const proposalId = await save(status);
    if (proposalId) navigate(`${PATHS.PROPOSES.VIEW}?id=${proposalId}`);
  };
  const selectedCodes = new Set(mobileItems.map((item) => item.product_code).filter(Boolean));
  const selectableOptions = (currentCode?: string) => productOptions.map((option) => ({
    ...option,
    disabled: option.value !== currentCode && selectedCodes.has(option.value),
  }));
  const openProductPicker = () => {
    setProductCodesToAdd([]);
    setProductPickerOpen(true);
    if (!products.length) void searchProductOptions();
  };

  if (loading) return <div className="propose-loading"><Spin /></div>;

  const productEditor = () => (
    <Form.List name="items">
      {(fields, { add, remove }) => (
        <>
          <div className="propose-product-toolbar">
            <div>
              <strong>担当営業が登録済みの商品</strong>
              <span>選択時点の担当 Sale を提案に固定します。</span>
            </div>
            <Button icon={<PlusOutlined />} onClick={openProductPicker}>商品を追加</Button>
          </div>
          {(() => {
            const productRows = fields.map((field) => {
              const value = mobileItems[field.name] || {};
              const product = productOptions.find((option) => option.value === value.product_code);
              return {
                key: field.key,
                field,
                product_code: value.product_code || "—",
                product_name: product?.name || value.product_name || "—",
              };
            });
            const selectedCount = productRows.filter((row) => selectedItemProductCodes.has(row.product_code)).length;
            const allSelected = productRows.length > 0 && selectedCount === productRows.length;
            const toggleSelection = (productCode: string, selected: boolean) => setSelectedItemProductCodes((current) => {
              const next = new Set(current);
              if (selected) next.add(productCode);
              else next.delete(productCode);
              return next;
            });
            const selectAll = (selected: boolean) => setSelectedItemProductCodes(
              selected ? new Set(productRows.map((row) => row.product_code)) : new Set(),
            );
            const removeSelected = () => Modal.confirm({
              title: "選択した商品を提案から削除しますか？",
              content: "削除した商品は、この提案書を保存するまで再度追加できます。",
              okText: "削除",
              cancelText: "キャンセル",
              okButtonProps: { danger: true },
              onOk: () => {
                [...productRows]
                  .reverse()
                  .filter((row) => selectedItemProductCodes.has(row.product_code))
                  .forEach((row) => remove(row.field.name));
                setSelectedItemProductCodes(new Set());
              },
            });
            const selectionActions = (
              <div className="propose-product-selection-actions">
                <span>{selectedCount}件選択</span>
                <Checkbox checked={allSelected} indeterminate={selectedCount > 0 && !allSelected} onChange={(event) => selectAll(event.target.checked)}>すべて選択</Checkbox>
                <Button type="link" size="small" disabled={!selectedCount} onClick={() => setSelectedItemProductCodes(new Set())}>選択解除</Button>
                <Button danger size="small" disabled={!selectedCount} onClick={removeSelected}>削除</Button>
              </div>
            );
            return <section className="propose-product-assignments" aria-label="対象商品">
              <div className="propose-product-list-header">
                <strong>対象商品</strong>
                {selectionActions}
              </div>
            <div className="propose-product-assignment-list">
              {productRows.map((row) => <article className={`propose-product-assignment${selectedItemProductCodes.has(row.product_code) ? " is-selected" : ""}`} key={row.key} onClick={() => toggleSelection(row.product_code, !selectedItemProductCodes.has(row.product_code))}>
                <Checkbox checked={selectedItemProductCodes.has(row.product_code)} onClick={(event) => event.stopPropagation()} onChange={(event) => toggleSelection(row.product_code, event.target.checked)} aria-label={`${row.product_code}を選択`} />
                <div className="propose-product-assignment-copy"><strong>{row.product_code}</strong><span>{row.product_name}</span></div>
              </article>)}
              {!fields.length && <div className="propose-product-empty">「商品を追加」から対象商品を選択してください。</div>}
            </div>
            </section>;
          })()}
          <Modal
            open={productPickerOpen}
            title="対象商品を追加"
            okText="選択した商品を追加"
            cancelText="キャンセル"
            okButtonProps={{ disabled: !productCodesToAdd.length }}
            onCancel={() => setProductPickerOpen(false)}
            onOk={() => {
              productCodesToAdd.forEach((productCode) => {
                const product = productOptions.find((option) => option.value === productCode);
                add({ product_code: productCode, product_name: product?.name || "" });
              });
              setProductPickerOpen(false);
              setProductCodesToAdd([]);
            }}
          >
            <p className="propose-product-picker-help">担当営業が登録済みの商品だけを選択できます。追加済みの商品は選択できません。</p>
            <Select
              mode="multiple"
              showSearch
              allowClear
              autoFocus
              style={{ width: "100%" }}
              placeholder="商品コード・商品名で検索"
              value={productCodesToAdd}
              options={selectableOptions()}
              filterOption={false}
              onSearch={searchProductOptions}
              onChange={setProductCodesToAdd}
            />
          </Modal>
        </>
      )}
    </Form.List>
  );

  return (
    <main className={isMobile ? "propose-mobile-editor-page propose-coordinator-editor" : "propose-editor"}>
      {isMobile ? (
        <div className="propose-mobile-form-content">
          <div>
            <h1>{id ? "提案を編集" : "新規提案を作成"}</h1>
            <span>基本情報と対象商品のみ登録します。価格・供給予定は担当 Sale が入力します。</span>
          </div>
        </div>
      ) : (
        <PageHeader
          title={id ? "提案を編集" : "新規提案を作成"}
          description="基本情報と対象商品のみ登録します。価格・供給予定は担当 Sale が入力します。"
          isbutton={false}
        />
      )}
      <Form form={form} layout="vertical" className="propose-form">
        <section className="propose-section">
          <h3>提案基本情報</h3>
          <div className="propose-section-body"><ProposeBasicInfoFields variant={isMobile ? "mobile" : "desktop"} customers={customers} recipients={recipients} /></div>
        </section>
        <section className="propose-section">
          <h3>対象商品</h3>
          <div className="propose-section-body">{productEditor()}</div>
        </section>
      </Form>
      <div className={isMobile ? "propose-mobile-editor-actions" : "propose-actions"}>
        <Button onClick={() => navigate(PATHS.PROPOSES.ROOT)}>キャンセル</Button>
        <Space><Button loading={saving} onClick={() => void saveProposal("DRAFT")}>下書き保存</Button><Button type="primary" loading={saving} onClick={() => void saveProposal("PUBLISH")}>営業へ公開</Button></Space>
      </div>
    </main>
  );
};
